<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { useResizeObserver, useScroll } from '@vueuse/core';
  import { ClubService, TClubMemberSortKey } from '@/api/services/club.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ModalClubMember from '@/components/modals/club/V2ModalClubMember.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { ClubMemberStatus, IClubMember } from '@/interfaces/IClubMember';
  import { IClubProgram } from '@/interfaces/IClubProgram';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { debounce } from '@/utils';

  const PAGE_SIZE = 20;

  interface IMembersColumn {
    key: TClubMemberSortKey;
    labelKey: string;
    /** Layout class shared with the row cells. */
    cellClass: string;
    /** First click: text A→Z; numbers and dates largest or newest first. */
    defaultDesc: boolean;
    isVisible: (program: IClubProgram) => boolean;
  }

  const DEFAULT_SORT_KEY: TClubMemberSortKey = 'createdAt';

  const ALL_COLUMNS: IMembersColumn[] = [
    {
      key: 'name',
      labelKey: 'club.members.colMember',
      cellClass: 'col-member',
      defaultDesc: false,
      isVisible: () => true,
    },
    {
      key: 'pointsBalance',
      labelKey: 'club.members.colPoints',
      cellClass: 'col-points',
      defaultDesc: true,
      isVisible: (program) => program.pointsEnabled,
    },
    {
      key: 'cashTotal',
      labelKey: 'club.members.colBalance',
      cellClass: 'col-balance',
      defaultDesc: true,
      isVisible: (program) => program.balanceEnabled,
    },
    {
      key: 'visitsCount',
      labelKey: 'club.members.colVisits',
      cellClass: 'col-visits',
      defaultDesc: true,
      isVisible: (program) => program.pointsEnabled,
    },
    {
      key: 'lastVisitAt',
      labelKey: 'club.members.colLastVisit',
      cellClass: 'col-last-visit',
      defaultDesc: true,
      isVisible: () => true,
    },
  ];

  const props = defineProps<{
    program: IClubProgram;
  }>();

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');

  const members = ref<IClubMember[] | null>(null);
  const total = ref(0);
  const page = ref(1);
  const search = ref('');
  const loading = ref(false);

  const columns = computed(() =>
    ALL_COLUMNS.filter((column) => column.isVisible(props.program))
  );

  // Phones cannot fit every column: the list scrolls sideways under a pinned
  // member column. The edges tell the state: a hairline on the pinned column
  // once the rest moved under it, a fade on the right while more is hidden.
  const scrollerRef = ref<HTMLElement>();
  const { arrivedState, measure } = useScroll(scrollerRef);

  // The arrived state only refreshes on scroll events: re-measure when the
  // scroller (re)mounts with the list or changes size (no overflow anymore).
  useResizeObserver(scrollerRef, measure);

  const sortKey = ref<TClubMemberSortKey>(DEFAULT_SORT_KEY);
  const sortDesc = ref(true);
  let loadSeq = 0;

  const totalPages = computed(() =>
    Math.max(1, Math.ceil(total.value / PAGE_SIZE))
  );

  async function load() {
    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    // Only the latest request may write state: sort and page clicks overlap.
    const seq = ++loadSeq;
    loading.value = true;
    try {
      const res = await ClubService.getMembers(restaurantId, {
        page: page.value,
        itemsPerPage: PAGE_SIZE,
        sortBy: [sortKey.value],
        sortDesc: [sortDesc.value],
        mustSort: true,
        search: search.value.trim(),
      });
      if (seq !== loadSeq) return;
      members.value = res.items;
      total.value = res.total;
    } catch (e) {
      if (seq === loadSeq) ui.serverError(e, i18nT('club.members.loadError'));
    } finally {
      if (seq === loadSeq) loading.value = false;
    }
  }

  const debouncedLoad = debounce(load, 400);

  watch(() => restaurantStore.restaurant?.id, load, { immediate: true });

  watch(search, () => {
    page.value = 1;
    debouncedLoad();
  });

  // One watcher for the three sources: Vue batches the mutations made by
  // onSort into a single load.
  watch([page, sortKey, sortDesc], load);

  // Program settings can change under this tab (PageClub refetches on the
  // clubU websocket event): fall back to the default order when the sorted
  // column disappears.
  watch(columns, (visible) => {
    if (sortKey.value === DEFAULT_SORT_KEY) return;
    if (visible.some((column) => column.key === sortKey.value)) return;
    sortKey.value = DEFAULT_SORT_KEY;
    sortDesc.value = true;
    page.value = 1;
  });

  function isSorted(column: IMembersColumn): boolean {
    return sortKey.value === column.key;
  }

  function onSort(column: IMembersColumn) {
    if (isSorted(column)) {
      sortDesc.value = !sortDesc.value;
    } else {
      sortKey.value = column.key;
      sortDesc.value = column.defaultDesc;
    }
    page.value = 1;
  }

  function isBlocked(member: IClubMember): boolean {
    return member.status === ClubMemberStatus.Blocked;
  }

  function formatDate(value: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  function openMember(member: IClubMember) {
    modals.show(
      V2ModalClubMember,
      { memberId: member.id, program: props.program },
      {
        name: 'v2-club-member',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 680,
        maxWidth: Math.round(window.innerWidth * 0.95),
      },
      {
        updated: (updated: IClubMember) => {
          if (!members.value) return;
          const idx = members.value.findIndex((m) => m.id === updated.id);
          if (idx > -1) members.value.splice(idx, 1, updated);
        },
      }
    );
  }
</script>

<template>
  <div class="v2-club-members">
    <div class="members-toolbar">
      <PosUiInput
        :value="search"
        icon="magnify"
        clearable
        :placeholder="$t('club.members.searchPlaceholder')"
        class="members-search"
        @input="search = $event"
      />
      <span v-if="members" class="members-count">
        {{ $t('club.members.count', { count: total }) }}
      </span>
    </div>

    <template v-if="!members">
      <div class="members-skeletons">
        <PosUiSkeleton v-for="n in 5" :key="n" class="skeleton-row" />
      </div>
    </template>

    <PosUiEmptyState
      v-else-if="members.length === 0"
      icon="account-group-outline"
      :title="
        search
          ? $t('club.members.emptySearchTitle')
          : $t('club.members.emptyTitle')
      "
      :description="search ? undefined : $t('club.members.emptyDescription')"
    />

    <template v-else>
      <div
        class="members-list"
        :class="{
          'is-scrolled': !arrivedState.left,
          'has-more': !arrivedState.right,
        }"
      >
        <div ref="scrollerRef" class="members-scroller">
          <div class="members-list-header">
            <span
              v-for="column in columns"
              :key="column.key"
              :class="column.cellClass"
            >
              <button
                type="button"
                class="sort-button"
                :class="{ 'is-active': isSorted(column) }"
                @click="onSort(column)"
              >
                {{ $t(column.labelKey) }}
                <i
                  v-if="isSorted(column)"
                  class="sort-icon mdi"
                  :class="sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up'"
                  aria-hidden="true"
                />
              </button>
            </span>
          </div>

          <button
            v-for="member in members"
            :key="member.id"
            class="member-row"
            type="button"
            @click="openMember(member)"
          >
            <span class="col-member">
              <span class="member-name">
                {{ member.name }}
                <PosUiBadge
                  v-if="isBlocked(member)"
                  color="error"
                  variant="soft"
                  size="xs"
                >
                  {{ $t('club.member.statusBlocked') }}
                </PosUiBadge>
              </span>
              <span class="member-sub">
                {{ $t('club.member.number', { number: member.memberNumber }) }}
                <template v-if="member.phone"> · {{ member.phone }}</template>
              </span>
            </span>
            <span v-if="program.pointsEnabled" class="col-points">
              {{ member.pointsBalance.toLocaleString('es-AR') }}
            </span>
            <span v-if="program.balanceEnabled" class="col-balance">
              {{ symbol }}{{ member.cashTotal.toLocaleString('es-AR') }}
            </span>
            <span v-if="program.pointsEnabled" class="col-visits">
              {{ member.visitsCount.toLocaleString('es-AR') }}
            </span>
            <span class="col-last-visit">
              {{ formatDate(member.lastVisitAt) }}
            </span>
          </button>
        </div>
      </div>

      <div v-if="totalPages > 1" class="members-pager">
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="chevron-left"
          :disabled="page <= 1 || loading"
          @click="page -= 1"
        />
        <span class="pager-info">
          {{ $t('club.members.pager', { page, total: totalPages }) }}
        </span>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="sm"
          icon="chevron-right"
          :disabled="page >= totalPages || loading"
          @click="page += 1"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-club-members {
    @apply tw-flex tw-flex-col tw-gap-3;

    .members-toolbar {
      @apply tw-flex tw-items-center tw-gap-3;

      .members-search {
        @apply tw-max-w-sm;
      }

      .members-count {
        @apply tw-text-sm tw-tabular-nums tw-text-neutral-500;
      }
    }

    .members-skeletons {
      @apply tw-flex tw-flex-col tw-gap-2;

      .skeleton-row {
        @apply tw-h-14 tw-rounded-xl;
      }
    }

    .members-list {
      @apply tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

      // Right-edge fade while more columns hide past the edge.
      &::after {
        content: '';
        @apply tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-10 tw-bg-gradient-to-l tw-from-white tw-to-transparent tw-opacity-0 tw-transition-opacity;
      }

      &.has-more::after {
        @apply tw-opacity-100;
      }

      // Columns never collapse: when they do not fit (phones) the list scrolls
      // sideways and the member column stays pinned on the left. Header and
      // rows size to their columns (min-w-max) so every row spans the whole
      // scrollable width; the padding lives in the first and last cells so
      // the pinned cell covers the rows sliding under it.
      .members-scroller {
        @apply tw-overflow-x-auto;
      }

      .members-list-header {
        @apply tw-flex tw-min-w-max tw-items-center tw-gap-3 tw-border-b tw-border-neutral-200 tw-bg-neutral-50 tw-pr-4 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;

        // The buttons carry the row height (44px touch target). Uppercase is
        // restated because preflight resets text-transform on buttons. Long
        // labels (some locales) wrap inside the cell and keep its alignment.
        .sort-button {
          @apply tw-inline-flex tw-min-h-11 tw-max-w-full tw-select-none tw-items-center tw-gap-1 tw-uppercase tw-transition-colors hover:tw-text-neutral-700;
          text-align: inherit;

          &.is-active {
            @apply tw-text-neutral-900;
          }

          .sort-icon {
            @apply tw-text-sm;
          }
        }
      }

      // Layout-only column classes shared by the header and the rows.
      // Pinned column: w-0 + flex-auto let it grow with the row on wide
      // screens while its min width (not the longest name) sets the row's
      // max-content width, so every row scrolls to the same width.
      .col-member {
        @apply tw-sticky tw-left-0 tw-flex tw-w-0 tw-min-w-[11rem] tw-flex-auto tw-flex-col tw-justify-center tw-self-stretch tw-bg-inherit tw-pl-4;

        // Edge hairline, shown while the other columns are scrolled under it.
        &::after {
          content: '';
          @apply tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-px tw-bg-neutral-200 tw-opacity-0 tw-transition-opacity;
        }
      }

      &.is-scrolled .col-member::after {
        @apply tw-opacity-100;
      }

      .col-points {
        @apply tw-w-20 tw-flex-none tw-text-right;
      }

      .col-balance {
        @apply tw-w-28 tw-flex-none tw-text-right;
      }

      .col-visits {
        @apply tw-w-24 tw-flex-none tw-text-right;
      }

      .col-last-visit {
        @apply tw-w-28 tw-flex-none tw-text-right;
      }

      .member-row {
        @apply tw-relative tw-flex tw-min-h-14 tw-w-full tw-min-w-max tw-items-center tw-gap-3 tw-border-b tw-border-neutral-100 tw-bg-white tw-pr-4 tw-text-left tw-transition-colors last:tw-border-b-0 hover:tw-bg-neutral-50;

        // The pinned cell paints over the row, so the focus ring goes on an
        // overlay above it.
        &:focus-visible {
          @apply tw-outline-none;

          &::after {
            content: '';
            @apply tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[1] tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
          }
        }

        .col-member {
          @apply tw-py-2;
        }

        .member-name {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-neutral-900;
        }

        .member-sub {
          @apply tw-text-xs tw-text-neutral-400;
        }

        .col-points,
        .col-balance,
        .col-visits {
          @apply tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
        }

        .col-last-visit {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }
    }

    .members-pager {
      @apply tw-flex tw-items-center tw-justify-center tw-gap-3;

      .pager-info {
        @apply tw-text-sm tw-tabular-nums tw-text-neutral-500;
      }
    }
  }
</style>
